import Table from "@/ui/table-div";
import { Token as TokenComponent } from "@/ui/token";
import {
  Price,
  Index,
  Volume,
  Liquidity,
  MarketCap,
  Trend,
  Age,
} from "@/ui/formats";
import { ScreenerToken } from "@api/token-screener/types.ts";
import { IconComponent, IconsTypes } from "@/ui/icon";
import IconButton from "@/ui/icon-button";
import Avatar from "@/ui/avatar";
import { useNavigate } from "react-router";
import {
  FiltersSort,
  ScreenerTokensParams,
  SortFieldKey,
  useGetScreenerTokens,
} from "@api/token-screener/screener-tokens.ts";
import { selectTokenScreenerTokens } from "@/api/token-screener/token-screener-tokens-selectors";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useTokenScreenerFilter } from "@providers/token-screener-filter/token-screener-filter-provider";
import clsx from "clsx";
import {
  toggleFavouriteToken,
  getFavouriteTokens,
  getHistoryTokens,
} from "@utils/token.ts";
import GradientBorderRadius from "@ui/gradient-border-radius.tsx";
import { UnstyledButton } from "@mantine/core";
import CopyButton from "@/features/copy/copy-button";
import { useWallet } from "@providers/wallet";
import { useBotTokenTraded } from "@api/bot/bot-get-token-traded.ts";

const transformNetworkToChain = (network: string) => {
  if (network === "ethereum") return "eth";
  if (network === "solana") return "sol";
  return network;
};

const TokenScreenerTable = () => {
  const navigate = useNavigate();
  const { appPubKey, currentWallet } = useWallet();

  const { data: tokensTraded } = useBotTokenTraded({
    appPubKey,
    chainId: currentWallet?.botChainId,
    walletAddress: currentWallet?.address,
  });

  const { filters, phrase, onFiltersUpdate, selection } =
    useTokenScreenerFilter();
  const refTableBody = useRef<HTMLDivElement>(null);

  const [favourites, setFavourites] = useState<string[]>(getFavouriteTokens());

  const [isLoadingIndication, setIsLoadingIndication] = useState(false);

  const {
    data: transformedData,
    isSuccess,
    isRefetching,
    isFetched,
  } = useGetScreenerTokens(
    {
      network: transformNetworkToChain("solana"),
      limit: 100,
      phrase: phrase,
      filters: filters,
    },
    selectTokenScreenerTokens
  );

  useEffect(() => {
    setIsLoadingIndication(true);
    if (!isSuccess || isRefetching) {
      return;
    }
    setTimeout(() => {
      setIsLoadingIndication(false);
    }, 1000);
  }, [isSuccess, isRefetching]);

  useEffect(() => {
    if (isFetched) {
      refTableBody.current?.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, [isFetched]);

  const toggleFavorite = (tokenId: string) => {
    const updated = toggleFavouriteToken(tokenId);
    setFavourites(updated);
    const selected = selection.showStarred ? "star" : selection.dropdownValue;

    if (selected === "star") {
      onFiltersUpdate((prev: ScreenerTokensParams["filters"]) => ({
        ...(prev ?? {}),
        tokens: updated,
      }));
    }
  };

  const handleSortToggle = useCallback(
    (field: SortFieldKey) => {
      return () => {
        onFiltersUpdate((prev: ScreenerTokensParams["filters"]) => {
          const prevSort = prev?.sort ?? {};
          const prevDirection = prevSort[field];
          const newDirection = prevDirection === "desc" ? "asc" : "desc";

          const trendingValue = prevSort.trending ?? null;

          const selected = selection.showStarred
            ? "star"
            : selection.dropdownValue;

          const newSort: FiltersSort = {
            trending: trendingValue,
            [field]: newDirection,
          };

          if (selected === "star") {
            return {
              ...prev,
              tokens: getFavouriteTokens(),
              sort: newSort,
            };
          }

          if (selected === "history") {
            return {
              ...prev,
              tokens: getHistoryTokens(),
              sort: newSort,
            };
          }

          if (selected === "positions") {
            return {
              ...prev,
              tokens: tokensTraded?.tokens ?? [],
              sort: newSort,
            };
          }

          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { tokens, ...rest } = prev ?? {};
          return {
            ...rest,
            sort: newSort,
          };
        });
      };
    },
    [selection, onFiltersUpdate]
  );

  return (
    <Table className="text-left">
      <Table.Header className="h-[1.5625rem] bg-black items-center">
        <Table.HeaderColumn className="w-[2.4%] flex justify-center items-center">
          #
        </Table.HeaderColumn>
        <Table.HeaderColumn className="w-[33%]">Token</Table.HeaderColumn>
        <Table.HeaderColumn
          className="w-[5.7%] cursor-pointer flex items-center gap-1"
          onClick={() => {
            handleSortToggle("age")();
          }}
        >
          Age
          <div className="w-[0.75rem] h-[0.75rem] flex items-center justify-start">
            {filters?.sort?.age &&
              (filters?.sort?.age === "asc" ? (
                <IconComponent
                  className="w-[0.375rem] h-[0.375rem]"
                  name="arrowDown"
                  color="white"
                />
              ) : (
                <IconComponent
                  className="w-[0.375rem] h-[0.375rem]"
                  name="arrowUp"
                  color="white"
                />
              ))}
          </div>
        </Table.HeaderColumn>
        <Table.HeaderColumn
          className="w-[5.7%] cursor-pointer flex items-center gap-1"
          onClick={() => handleSortToggle("txns")()}
        >
          Txns
          <div className="w-[0.75rem] h-[0.75rem]flex items-center justify-start">
            {filters?.sort?.txns &&
              (filters?.sort?.txns === "desc" ? (
                <IconComponent
                  className="w-[0.375rem] h-[0.375rem]"
                  name="arrowDown"
                  color="white"
                />
              ) : (
                <IconComponent
                  className="w-[0.375rem] h-[0.375rem]"
                  name="arrowUp"
                  color="white"
                />
              ))}
          </div>
        </Table.HeaderColumn>
        <Table.HeaderColumn
          className="w-[5.7%] cursor-pointer flex items-center gap-1"
          onClick={() => handleSortToggle("makers")()}
        >
          Wallets
          <div className="w-[0.75rem] h-[0.75rem]flex items-center justify-start">
            {filters?.sort?.makers &&
              (filters?.sort?.makers === "desc" ? (
                <IconComponent
                  className="w-[0.375rem] h-[0.375rem]"
                  name="arrowDown"
                  color="white"
                />
              ) : (
                <IconComponent
                  className="w-[0.375rem] h-[0.375rem]"
                  name="arrowUp"
                  color="white"
                />
              ))}
          </div>
        </Table.HeaderColumn>
        <Table.HeaderColumn
          className="w-[5.7%] cursor-pointer flex items-center gap-1"
          onClick={() => handleSortToggle("holders")()}
        >
          Holders
          <div className="w-[0.75rem] h-[0.75rem] flex items-center gap-1">
            {filters?.sort?.holders &&
              (filters?.sort?.holders === "desc" ? (
                <IconComponent
                  className="w-[0.375rem] h-[0.375rem]"
                  name="arrowDown"
                  color="white"
                />
              ) : (
                <IconComponent
                  className="w-[0.375rem] h-[0.375rem]"
                  name="arrowUp"
                  color="white"
                />
              ))}
          </div>
        </Table.HeaderColumn>
        <Table.HeaderColumn
          className="w-[5.7%] cursor-pointer flex items-center gap-1"
          onClick={() => handleSortToggle("volume")()}
        >
          Volume
          <div className="w-[0.75rem] h-[0.75rem]flex items-center justify-start">
            {filters?.sort?.volume &&
              (filters?.sort?.volume === "desc" ? (
                <IconComponent
                  className="w-[0.375rem] h-[0.375rem]"
                  name="arrowDown"
                  color="white"
                />
              ) : (
                <IconComponent
                  className="w-[0.375rem] h-[0.375rem]"
                  name="arrowUp"
                  color="white"
                />
              ))}
          </div>
        </Table.HeaderColumn>
        <Table.HeaderColumn
          className="w-[5.7%] cursor-pointer flex items-center gap-1"
          onClick={() => handleSortToggle("liquidity")()}
        >
          Liquidity
          <div className="w-[0.75rem] h-[0.75rem] flex items-center gap-1">
            {filters?.sort?.liquidity &&
              (filters?.sort?.liquidity === "desc" ? (
                <IconComponent
                  className="w-[0.375rem] h-[0.375rem]"
                  name="arrowDown"
                  color="white"
                />
              ) : (
                <IconComponent
                  className="w-[0.375rem] h-[0.375rem]"
                  name="arrowUp"
                  color="white"
                />
              ))}
          </div>
        </Table.HeaderColumn>
        <Table.HeaderColumn
          className="w-[7%] cursor-pointer flex items-center gap-1"
          onClick={() => handleSortToggle("marketCap")()}
        >
          Market Cap
          <div className="w-[0.75rem] h-[0.75rem]flex items-center justify-start">
            {filters?.sort?.marketCap &&
              (filters?.sort?.marketCap === "desc" ? (
                <IconComponent
                  className="w-[0.375rem] h-[0.375rem]"
                  name="arrowDown"
                  color="white"
                />
              ) : (
                <IconComponent
                  className="w-[0.375rem] h-[0.375rem]"
                  name="arrowUp"
                  color="white"
                />
              ))}
          </div>
        </Table.HeaderColumn>
        <Table.HeaderColumn
          className="w-[7%] cursor-pointer flex items-center gap-1"
          onClick={() => handleSortToggle("price")()}
        >
          Price
          <div className="w-[0.75rem] h-[0.75rem]flex items-center justify-start">
            {filters?.sort?.price &&
              (filters?.sort?.price === "desc" ? (
                <IconComponent
                  className="w-[0.375rem] h-[0.375rem]"
                  name="arrowDown"
                  color="white"
                />
              ) : (
                <IconComponent
                  className="w-[0.375rem] h-[0.375rem]"
                  name="arrowUp"
                  color="white"
                />
              ))}
          </div>
        </Table.HeaderColumn>
        <Table.HeaderColumn
          className="w-[5.7%] cursor-pointer flex items-center gap-1"
          onClick={() => handleSortToggle("priceChange1Hour")()}
        >
          1H
          <div className="w-[0.75rem] h-[0.75rem]flex items-center justify-start">
            {filters?.sort?.priceChange1Hour &&
              (filters?.sort?.priceChange1Hour === "desc" ? (
                <IconComponent
                  className="w-[0.375rem] h-[0.375rem]"
                  name="arrowDown"
                  color="white"
                />
              ) : (
                <IconComponent
                  className="w-[0.375rem] h-[0.375rem]"
                  name="arrowUp"
                  color="white"
                />
              ))}
          </div>
        </Table.HeaderColumn>
        <Table.HeaderColumn
          className="w-[5.7%] cursor-pointer flex items-center gap-1"
          onClick={() => handleSortToggle("priceChange24Hours")()}
        >
          24H
          <div className="w-[0.75rem] h-[0.75rem]flex items-center justify-start">
            {filters?.sort?.priceChange24Hours &&
              (filters?.sort?.priceChange24Hours === "desc" ? (
                <IconComponent
                  className="w-[0.375rem] h-[0.375rem]"
                  name="arrowDown"
                  color="white"
                />
              ) : (
                <IconComponent
                  className="w-[0.375rem] h-[0.375rem]"
                  name="arrowUp"
                  color="white"
                />
              ))}
          </div>
        </Table.HeaderColumn>
        <Table.HeaderColumn className="text-center w-[5.7%]">
          Exchange
        </Table.HeaderColumn>
      </Table.Header>
      {isLoadingIndication && (
        <div className="w-full h-[0.0625rem] bg-black overflow-hidden">
          <div className="w-full h-full bg-[#5AC4BE] animate-[loading_1s_ease-in-out_infinite]" />
        </div>
      )}
      {/* @ts-expect-error - ref is not typed */}
      <Table.Body id="token-screener-table" ref={refTableBody}>
        {transformedData?.results
          ?.filter(
            (item: ScreenerToken) =>
              item.exchange !== null && item.exchange !== "null"
          )
          .map((item: ScreenerToken, index: number) => {
            const isFavourite = favourites.includes(item.tokenId);
            return (
              <Fragment key={`row-${item.tokenId}-${item.name}-fragment`}>
                <Table.Row
                  key={`row-${item.tokenId}-${item.name}-row`}
                  onClick={() => {
                    navigate(`/screener/${item.tokenId}`);
                  }}
                  className={clsx("h-[2rem] text-xs z-0 flex items-center")}
                >
                  <Table.Column className="w-[2.4%] flex justify-center items-center font-semibold">
                    <Index value={index + 1} />
                  </Table.Column>
                  <Table.Column className="w-[33%]">
                    <div className="flex justify-start items-center">
                      <div className="w-[1.1875rem] h-[1.1875rem] rounded-full flex justify-center items-center bg-black">
                        <IconComponent
                          name={item.networkLogo as IconsTypes}
                          className="w-[1.1875rem] h-[1.1875rem]"
                        />
                      </div>
                      <TokenComponent
                        name={item.name}
                        imageUrl={item.icon}
                        symbol={item.symbol}
                      />
                      <div className="ml-[0.625rem] flex justify-start items-center gap-[0.1875rem]">
                        <CopyButton
                          value={item.address}
                          className="w-[1.125rem] h-[1.125rem]"
                          iconClassName="w-[0.5625rem] h-[0.5625rem]"
                        />
                        {item.socialLinks?.website && (
                          <IconButton
                            withHover={true}
                            withBorder={false}
                            name="internetPlanet"
                            variant="circle"
                            className="w-[1.125rem] h-[1.125rem]  bg-black rounded-full"
                            iconClassName="w-[0.5625rem] h-[0.5625rem]"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(item.socialLinks.website, "_blank");
                            }}
                          />
                        )}
                        {item.socialLinks?.twitter && (
                          <IconButton
                            withHover={true}
                            withBorder={false}
                            name="socialX"
                            variant="circle"
                            className="w-[1.125rem] h-[1.125rem]  bg-black rounded-full"
                            iconClassName="w-[0.4375rem] h-[0.4375rem]"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(item.socialLinks.twitter, "_blank");
                            }}
                          />
                        )}
                        {item.socialLinks?.telegram && (
                          <IconButton
                            withHover={true}
                            withBorder={false}
                            name="socialTelegram"
                            variant="circle"
                            className="w-[1.125rem] h-[1.125rem]  bg-black rounded-full"
                            iconClassName="w-[0.5rem] h-[0.5rem]"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(item.socialLinks.telegram, "_blank");
                            }}
                          />
                        )}
                        <GradientBorderRadius
                          hover="r-100"
                          radius="circle"
                          gradient="none"
                          background="black"
                        >
                          <UnstyledButton
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFavorite(item.tokenId ?? "");
                            }}
                            className={clsx(
                              "w-[1.0625rem] h-[1.0625rem] flex justify-center items-center hover:opacity-100",
                              "opacity-100"
                            )}
                          >
                            <IconComponent
                              name="starEmpty"
                              className="w-[0.6875rem] h-[0.6875rem]"
                              fill={isFavourite ? "white" : "none"}
                            />
                          </UnstyledButton>
                        </GradientBorderRadius>
                      </div>
                    </div>
                  </Table.Column>
                  <Table.Column className="w-[5.7%]">
                    <Age value={Date.parse(item.age) as number} />
                  </Table.Column>
                  <Table.Column className="w-[5.7%]">{item.txns}</Table.Column>
                  <Table.Column className="w-[5.7%]">
                    {item.makers}
                  </Table.Column>
                  <Table.Column className="w-[5.7%]">
                    {item.holders ? item.holders : "-"}
                  </Table.Column>
                  <Table.Column className="w-[5.7%]">
                    <Volume value={item.volume} />
                  </Table.Column>
                  <Table.Column className="w-[5.7%]">
                    <Liquidity value={item.liquidity} />
                  </Table.Column>
                  <Table.Column className="w-[7%]">
                    <MarketCap value={item.marketCap} />
                  </Table.Column>
                  <Table.Column className="w-[7%]">
                    <Price value={item.price} decimals={7} />
                  </Table.Column>
                  <Table.Column className="w-[5.7%]">
                    <Trend
                      value={item.priceChange1Hour}
                      withSign={false}
                      withTrendArrow={true}
                    />
                  </Table.Column>
                  <Table.Column className="w-[5.7%]">
                    <Trend
                      value={item.priceChange24Hours}
                      withSign={false}
                      withTrendArrow={true}
                    />
                  </Table.Column>
                  <Table.Column className="flex justify-center items-center w-[5.7%]">
                    <div className="w-[1.1875rem] h-[1.875rem] flex justify-center items-center ">
                      <Avatar source={item.exchange} size="bigger" />
                    </div>
                  </Table.Column>
                </Table.Row>
              </Fragment>
            );
          })}
      </Table.Body>
    </Table>
  );
};
export default TokenScreenerTable;
